import addNewTextToPageOperation from 'editor/src/store/design/operation/addNewTextToPageOperation';
import updateMediaElementOperation, {
  MediaUpdateActionName,
} from 'editor/src/store/design/operation/updateMediaElementOperation';
import { getStructureIndexByElementUuid } from 'editor/src/store/design/selector/getStructureIndexByElementUuid';
import { MediaText } from 'editor/src/store/design/types';
import updateTextPropertiesWithoutRender from 'editor/src/store/design/util/updateTextPropertiesWithoutRender';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import type { Thunk } from 'editor/src/store/hooks';

import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

import getTextLibraryAppendTarget from '../selector/getTextLibraryAppendTarget';
import { TextLibraryEntry } from '../types';
import buildTextLibraryText from '../util/buildTextLibraryText';

// a blank line between entries, as between verses
const ENTRY_SEPARATOR = '\n\n';

/**
 * Adds the entry's chosen verses (and title, unless left out) as a plain copy, not linked to the library.
 *
 * With one editable text selected, the entry goes after its content: the text keeps its own frame and style, so a
 * template's prepared frame fills up entry by entry (a fixed frame then shows the overflow marker when full).
 * Otherwise it becomes a new text on the current page.
 * Returns whether the text was placed (the current page may not allow new elements).
 */
const insertTextLibraryEntryOperation =
  (entry: TextLibraryEntry, verseIndexes: number[], includeTitle = true): Thunk =>
  (dispatch, getState) => {
    const state = getState();
    const text = buildTextLibraryText(entry, verseIndexes, includeTitle);
    const target = getTextLibraryAppendTarget(state);
    const address = target && getStructureIndexByElementUuid(state.design.designData, target.uuid);

    if (target && address) {
      const current = target.extra.text.trimEnd();
      const elementUpdate: Partial<MediaText> = {
        extra: { ...target.extra, text: current ? `${current}${ENTRY_SEPARATOR}${text}` : text },
      };
      // the caller measures; the operation keeps a fixed frame's height
      updateTextPropertiesWithoutRender(target, elementUpdate, undefined);
      dispatch(updateMediaElementOperation(address, elementUpdate, MediaUpdateActionName.TEXT_UPDATED));
    } else {
      const spreadIndex = getCurrentSpreadIndex(state);
      const elementUuid: number | undefined = dispatch(
        addNewTextToPageOperation(spreadIndex, 0, text, undefined, { keepFullText: true, enterTextEditMode: false }),
      );
      if (elementUuid === undefined) {
        return false;
      }
    }

    sendPostMessage('log.textLibraryEntryInserted', {
      id: entry.id,
      number: entry.number,
      verseCount: verseIndexes.length,
      appended: !!(target && address),
    });
    return true;
  };

export default insertTextLibraryEntryOperation;
